import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import axios from 'axios';

const ActivateCollaborator = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Token d&apos;activation manquant');
    }
  }, [token]);

  const validatePassword = (pwd) => {
    if (pwd.length < 8) {
      return 'Le mot de passe doit contenir au moins 8 caractères';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/activate-collaborator`,
        {
          token: token,
          password: password,
          confirm_password: confirmPassword
        }
      );

      setSuccess(true);
      
      // Stocker le token d'accès
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
      }

      // Rediriger vers le dashboard après 2 secondes
      setTimeout(() => {
        navigate('/client-dashboard');
      }, 2000);

    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l&apos;activation du compte');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={48} className="text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-4">
            ✅ Compte activé !
          </h1>
          <p className="text-slate-600 mb-4">
            Votre mot de passe a été créé avec succès.
          </p>
          <p className="text-sm text-slate-500">
            Redirection vers votre espace client...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock size={32} className="text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Activation de votre compte
          </h1>
          <p className="text-slate-600">
            Créez votre mot de passe pour accéder à l&apos;espace client
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
            <AlertCircle size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Mot de passe */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Mot de passe *
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-12"
                placeholder="Minimum 8 caractères"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Confirmation mot de passe */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Confirmer le mot de passe *
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-12"
                placeholder="Confirmer votre mot de passe"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Indicateur de force du mot de passe */}
          {password && (
            <div className="space-y-2">
              <p className="text-xs text-slate-600">Force du mot de passe :</p>
              <div className="flex space-x-1">
                {[1, 2, 3, 4].map((level) => {
                  let strength = 0;
                  if (password.length >= 8) strength = 1;
                  if (password.length >= 12) strength = 2;
                  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) strength = Math.max(strength, 2);
                  if (/[0-9]/.test(password)) strength = Math.max(strength, 3);
                  if (/[^A-Za-z0-9]/.test(password)) strength = 4;

                  const colors = ['bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-green-500'];
                  
                  return (
                    <div
                      key={level}
                      className={`h-2 flex-1 rounded ${
                        level <= strength ? colors[strength - 1] : 'bg-slate-200'
                      }`}
                    />
                  );
                })}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !token}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader size={20} className="animate-spin" />
                <span>Activation en cours...</span>
              </>
            ) : (
              <>
                <Lock size={20} />
                <span>Créer mon mot de passe</span>
              </>
            )}
          </button>
        </form>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800">
            💡 <strong>Conseil :</strong> Choisissez un mot de passe fort contenant des majuscules, minuscules, chiffres et caractères spéciaux.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ActivateCollaborator;
